import type { StockingSeason } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Zero-based month index of the last spring month (June), as in the naming convention. */
const LAST_SPRING_MONTH = 5;

/** ISO 8601: a year has 53 weeks when 1 January is a Thursday, or a Wednesday in a leap year. */
export function isoWeeksInYear(year: number): number {
    const jan1 = new Date(Date.UTC(year, 0, 1)).getUTCDay();
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return jan1 === 4 || (isLeap && jan1 === 3) ? 53 : 52;
}

/** The Monday of ISO week `week` of `year`, in UTC. */
export function isoWeekMonday(year: number, week: number): Date {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Weekday = jan4.getUTCDay() || 7;
    const week1Monday = jan4.getTime() - (jan4Weekday - 1) * DAY_MS;
    return new Date(week1Monday + (week - 1) * 7 * DAY_MS);
}

/**
 * The `plannedStartDate` the backend stores for a planned week: that week's Monday, as a
 * calendar day. A cycle has a date, not a week, so the week is only a way of picking one.
 */
export function plannedStartDateForWeek(year: number, week: number): string {
    return isoWeekMonday(year, week).toISOString().slice(0, 10);
}

/**
 * The season the backend derives from a planned week's Monday. The modal lets the customer pick
 * the season, so this is only used to point out a week that falls in the other one.
 */
export function seasonOfPlannedWeek(year: number, week: number): StockingSeason {
    return seasonOfDay(isoWeekMonday(year, week));
}

/** The season of a calendar day held as UTC midnight, as the backend reads a `@db.Date`. */
export function seasonOfDay(day: Date): StockingSeason {
    return day.getUTCMonth() <= LAST_SPRING_MONTH ? 'spring' : 'autumn';
}

export function isValidPlannedWeek(year: number, week: number): boolean {
    return Number.isInteger(week) && week >= 1 && week <= isoWeeksInYear(year);
}
